import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CurrencyInput from 'react-currency-input-field';
import DataTable from 'react-data-table-component';

import { detallesNomina, crearNomina, editarNomina, limpiarNomina } from '../../actions/nominas';
import { buscarSucursales } from '../../actions/sucursales';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    control: (provided) => ({
        ...provided,
        paddingTop: '5px',
        paddingBottom: '5px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        overflow: 'visible',
        marginTop: '10px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const tiposEmpleado = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' },
    { value: 3, label: 'Freidor' },
    { value: 4, label: 'Vendedor ambulante' },
    { value: 5, label: 'Administrador' }
];

const tiposConcepto = [
    { value: 0, label: 'Bono' },
    { value: 1, label: 'Préstamo' },
    { value: 2, label: 'Descuento' }
];

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    : '-';

const FormularioNomina = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const esEdicion = !!id;

    const detalles = useSelector(state => state.nominas.detalles);
    const { listado: sucursalesListado } = useSelector(state => state.sucursales);

    const [cargado, setCargado] = useState(false);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
    const [asistenciaEmpleado, setAsistenciaEmpleado] = useState(null);
    const [conesEmpleado,     setConesEmpleado]     = useState(null);

    /* Estado del formulario */
    const [nominaData, setNominaData] = useState({
        observaciones: '',
        fecha_fin: null,
        empleados: []
    });

    const [statusProcesar, setStatusProcesar] = useState(false);

    /* ── useEffects ── */
    useEffect(() => {
        return () => { dispatch(limpiarNomina()); };
    }, []);

    useEffect(() => {
        if (esEdicion && id) {
            dispatch(detallesNomina({ id }));
            dispatch(editarBreadcrumb([
                { nombre: 'Nóminas', path: '/nominas', activo: false },
                { nombre: 'Editar nómina', path: `/nominas/editar/${id}`, activo: true }
            ]));
        } else {
            dispatch(buscarSucursales());
            dispatch(editarBreadcrumb([
                { nombre: 'Nóminas', path: '/nominas', activo: false },
                { nombre: 'Nueva nómina', path: '/nominas/nuevo', activo: true }
            ]));
        }
    }, [id]);

    useEffect(() => {
        if (detalles) {
            setCargado(true);
            setNominaData({
                observaciones: detalles.observaciones || '',
                fecha_fin: detalles.fecha_fin ? new Date(detalles.fecha_fin) : null,
                empleados: (detalles.empleados || []).map(emp => ({
                    empleado: emp.empleado?._id || emp.empleado,
                    observaciones: emp.observaciones || '',
                    conceptos: (emp.conceptos || []).map(c => ({
                        tipo: c.tipo,
                        concepto: c.concepto || '',
                        total: c.total != null ? String(c.total) : '',
                        fecha: c.fecha ? new Date(c.fecha) : null
                    }))
                }))
            });
        }
    }, [detalles]);

    /* ── Confirmar sucursal (solo creación) ── */
    const handleConfirmarSucursal = () => {
        if (!sucursalSeleccionada) return;
        dispatch(detallesNomina({ id: null, sucursal: sucursalSeleccionada.value }));
    };

    /* ── Handlers por empleado ── */
    const handleObservacionesEmpleado = (empIndex, value) => {
        const nuevos = [...nominaData.empleados];
        nuevos[empIndex].observaciones = value;
        setNominaData({ ...nominaData, empleados: nuevos });
    };

    const handleAgregarConcepto = (empIndex) => {
        const nuevos = [...nominaData.empleados];
        nuevos[empIndex].conceptos = [...nuevos[empIndex].conceptos, { tipo: null, concepto: '', total: '', fecha: null }];
        setNominaData({ ...nominaData, empleados: nuevos });
    };

    const handleEliminarConcepto = (empIndex, concIndex) => {
        const nuevos = [...nominaData.empleados];
        nuevos[empIndex].conceptos = nuevos[empIndex].conceptos.filter((_, i) => i !== concIndex);
        setNominaData({ ...nominaData, empleados: nuevos });
    };

    const handleConceptoChange = (empIndex, concIndex, field, value) => {
        const nuevos = [...nominaData.empleados];
        nuevos[empIndex].conceptos[concIndex][field] = value;
        setNominaData({ ...nominaData, empleados: nuevos });
    };

    /* ── Submit ── */
    const handleSubmit = async (tipo = 0) => {
        setStatusProcesar(true);

        const payload = {
            tipo,
            observaciones: nominaData.observaciones,
            fecha_fin: nominaData.fecha_fin ? new Date(new Date(nominaData.fecha_fin).setHours(23, 59, 59, 999)) : null,
            ...(!esEdicion && { sucursal: sucursalSeleccionada?.value }),
            empleados: nominaData.empleados.map(emp => ({
                ...emp,
                conceptos: emp.conceptos.map(c => ({
                    ...c,
                    total: parseFloat(c.total) || 0
                }))
            }))
        };

        let ok;
        if (esEdicion) {
            ok = await dispatch(editarNomina({ id: detalles._id, ...payload }));
        } else {
            ok = await dispatch(crearNomina(payload));
        }

        setStatusProcesar(false);
        if (ok) navigate('/nominas');
    };

    /* ── Helpers de display ── */
    const soloLectura = detalles?.status === true;

    const tipoLabel = (tipo) => tiposEmpleado.find(t => t.value === tipo)?.label || '-';

    const totales = (detalles?.empleados || []).reduce((acc, empDetalle, i) => {
        const conceptos = nominaData.empleados[i]?.conceptos || [];
        const bonos     = conceptos.filter(c => c.tipo === 0).reduce((s, c) => s + (parseFloat(c.total) || 0), 0);
        const prestamos = conceptos.filter(c => c.tipo === 1).reduce((s, c) => s + (parseFloat(c.total) || 0), 0);
        const descuentos = conceptos.filter(c => c.tipo === 2).reduce((s, c) => s + (parseFloat(c.total) || 0), 0);
        acc.sueldo      += empDetalle.sueldo      || 0;
        acc.comisiones  += empDetalle.comisiones  || 0;
        acc.bonos       += bonos;
        acc.prestamos   += prestamos;
        acc.descuentos  += descuentos;
        return acc;
    }, { sueldo: 0, comisiones: 0, bonos: 0, prestamos: 0, descuentos: 0 });
    totales.total = totales.sueldo + totales.comisiones + totales.bonos - totales.prestamos - totales.descuentos;

    return (
        <div className="row mt-5">

            {/* Título */}
            <div className="col-12 mb-4">
                <h4 className="mb-0">{esEdicion ? 'Editar nómina' : 'Nueva nómina'}</h4>
                {soloLectura && (
                    <span className="badge bg-success mt-2 fs-6 px-3 py-2">
                        <i className="fas fa-lock me-1"></i> Nómina finalizada — solo lectura
                    </span>
                )}
            </div>

            {/* ── SELECTOR DE SUCURSAL (solo creación, antes de cargar empleados) ── */}
            {!esEdicion && !cargado && (
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <h6 className="fw-semibold mb-1"><i className="fas fa-store text-primary me-2"></i>Selecciona la sucursal</h6>
                            <p className="text-muted small mb-3">Elige la sucursal para cargar los empleados y datos del periodo.</p>
                            <div className="row g-3 align-items-end">
                                <div className="col-md-5 form-group">
                                    <div className="input-group h-100">
                                        <Select
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                            placeholder="Sucursal"
                                            options={(sucursalesListado || []).map(s => ({ value: s._id, label: s.nombre }))}
                                            value={sucursalSeleccionada}
                                            onChange={setSucursalSeleccionada}
                                            menuPortalTarget={document.body}
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            isClearable
                                        />
                                    </div>
                                </div>
                                <div className="col-md-auto">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleConfirmarSucursal}
                                        disabled={!sucursalSeleccionada}
                                    >
                                        <i className="fas fa-arrow-right me-1"></i> Continuar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── FORMULARIO COMPLETO (cuando ya hay detalles cargados) ── */}
            {cargado && (
                <>
                    <div className="col-12">
                        <div className="accordion" id="accordionNomina">

                            {/* ── Sección 1: Información general ── */}
                            <div className="accordion-item border-0 shadow-sm mb-2">
                                <h2 className="accordion-header" id="headingGeneral">
                                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseGeneral" aria-expanded="true" aria-controls="collapseGeneral">
                                        <i className="fas fa-info-circle text-primary me-2"></i>
                                        Información general
                                    </button>
                                </h2>
                                <div id="collapseGeneral" className="accordion-collapse collapse show" aria-labelledby="headingGeneral" data-bs-parent="#accordionNomina">
                                    <div className="accordion-body">
                                        <div className="row g-3">

                                            {/* Sucursal (solo lectura) */}
                                            <div className="col-md-3">
                                                <div className="border-start border-primary border-3 ps-3">
                                                    <div className="text-muted small mb-1">Sucursal</div>
                                                    <div className="fw-semibold">{detalles?.sucursal?.nombre || sucursalSeleccionada?.label || '-'}</div>
                                                </div>
                                            </div>

                                            {/* Responsable (solo lectura) */}
                                            <div className="col-md-3">
                                                <div className="border-start border-primary border-3 ps-3">
                                                    <div className="text-muted small mb-1">Responsable</div>
                                                    <div className="fw-semibold">{formatNombre(detalles?.responsable)}</div>
                                                </div>
                                            </div>

                                            {/* Fecha inicio (solo lectura) */}
                                            <div className="col-md-3">
                                                <div className="border-start border-secondary border-3 ps-3">
                                                    <div className="text-muted small mb-1">Fecha inicio</div>
                                                    <div className="fw-semibold">{formatFecha(detalles?.fecha_inicio)}</div>
                                                </div>
                                            </div>

                                            {/* Fecha fin */}
                                            <div className="col-md-3">
                                                {soloLectura ? (
                                                    <div className="border-start border-secondary border-3 ps-3">
                                                        <div className="text-muted small mb-1">Fecha fin</div>
                                                        <div className="fw-semibold">{formatFecha(detalles?.fecha_fin) || 'Pendiente'}</div>
                                                    </div>
                                                ) : (
                                                    <div className="input-group">
                                                        <div className="form-floating">
                                                            <DatePicker
                                                                id="fecha_fin_nomina"
                                                                className="form-control text-center input-datepicker"
                                                                selected={nominaData.fecha_fin}
                                                                onChange={date => setNominaData({ ...nominaData, fecha_fin: date })}
                                                                dateFormat="dd/MM/yyyy"
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                dropdownMode="select"
                                                                minDate={detalles?.fecha_inicio ? new Date(detalles.fecha_inicio) : undefined}
                                                                isClearable
                                                                autoComplete="off"
                                                            />
                                                            <label className="label-datepicker" htmlFor="fecha_fin_nomina">Fecha fin</label>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Indicadores de totales */}
                                            <div className="col-12">
                                                <div className="row g-2">
                                                    {[
                                                        { label: 'Total sueldo',       value: totales.sueldo,       color: 'success' },
                                                        { label: 'Total comisiones',   value: totales.comisiones,   color: 'success'      },
                                                        { label: 'Total bonos',       value: totales.bonos,      color: 'success'   },
                                                        { label: 'Total préstamos',   value: totales.prestamos,  color: 'danger'      },
                                                        { label: 'Total descuentos',  value: totales.descuentos, color: 'danger'    },
                                                        { label: 'Total nómina',       value: totales.total,        color: 'warning'   },
                                                    ].map(({ label, value, color }) => (
                                                        <div className="col-md" key={label}>
                                                            <div className={`border-start border-${color} border-3 rounded-end bg-light px-3 py-2`}>
                                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
                                                                <div className={`fw-bold text-${color}`}>{formatNumber(value, 2, '$', '')}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Observaciones generales */}
                                            <div className="col-12">
                                                {soloLectura ? (
                                                    <div className="border-start border-warning border-3 ps-3">
                                                        <div className="text-muted small mb-1">Observaciones</div>
                                                        <div className="fw-semibold">{detalles?.observaciones || 'Sin observaciones'}</div>
                                                    </div>
                                                ) : (
                                                    <div className="form-floating">
                                                        <textarea
                                                            className="form-control"
                                                            placeholder="Observaciones"
                                                            id="observaciones"
                                                            style={{ height: '90px' }}
                                                            value={nominaData.observaciones}
                                                            onChange={({ target }) => setNominaData({ ...nominaData, observaciones: target.value })}
                                                        ></textarea>
                                                        <label htmlFor="observaciones">Observaciones</label>
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Sección 2: Empleados ── */}
                            <div className="accordion-item border-0 shadow-sm mb-2">
                                <h2 className="accordion-header" id="headingEmpleados">
                                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseEmpleados" aria-expanded="false" aria-controls="collapseEmpleados">
                                        <i className="fas fa-users text-success me-2"></i>
                                        Empleados
                                        <span className="badge bg-success rounded-pill ms-2">{detalles?.empleados?.length || 0}</span>
                                    </button>
                                </h2>
                                <div id="collapseEmpleados" className="accordion-collapse collapse" aria-labelledby="headingEmpleados" data-bs-parent="#accordionNomina">
                                    <div className="accordion-body">

                                        {(detalles?.empleados || []).length === 0 ? (
                                            <p className="text-muted mb-0">No hay empleados en esta nómina.</p>
                                        ) : (
                                            (detalles.empleados).map((empDetalle, empIndex) => {

                                                const empForm = nominaData.empleados[empIndex];
                                                const tipoEmp = tipoLabel(empDetalle.empleado?.tipo);

                                                const bonos = (empForm?.conceptos || [])
                                                    .filter(c => c.tipo === 0)
                                                    .reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
                                                const prestamos = (empForm?.conceptos || [])
                                                    .filter(c => c.tipo === 1)
                                                    .reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
                                                const descuentos = (empForm?.conceptos || [])
                                                    .filter(c => c.tipo === 2)
                                                    .reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
                                                const totalEmpleado = (empDetalle.sueldo || 0) + (empDetalle.comisiones || 0) + bonos - prestamos - descuentos;

                                                return (
                                                    <div key={empDetalle._id || empIndex} className="card border border-light mb-3">

                                                        {/* Cabecera del empleado */}
                                                        <div className="card-header bg-light d-flex align-items-center gap-2 py-2">
                                                            <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, minWidth: 36 }}>
                                                                <i className="fas fa-user text-primary small"></i>
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold">{formatNombre(empDetalle.empleado)}</div>
                                                            </div>
                                                        </div>

                                                        <div className="card-body">

                                                            {/* Resumen calculado (solo lectura) */}
                                                            <div className="row g-2 mb-3">
                                                                {[
                                                                    { label: 'Salario diario',  value: empDetalle.salario,        color: 'info',    raw: false },
                                                                    { label: 'Días trabajados', value: empDetalle.dias_trabajados, color: 'info',    raw: true,  onClick: () => setAsistenciaEmpleado(empDetalle) },
                                                                    { label: 'Sueldo',          value: empDetalle.sueldo,          color: 'success', raw: false },
                                                                    { label: 'Comisiones',      value: empDetalle.comisiones,      color: 'success', raw: false, onClick: () => setConesEmpleado(empDetalle) },
                                                                    { label: 'Bonos',           value: bonos,                      color: 'success', raw: false },
                                                                    { label: 'Préstamos',       value: prestamos,                  color: 'danger',  raw: false },
                                                                    { label: 'Descuentos',      value: descuentos,                 color: 'danger',  raw: false },
                                                                    { label: 'Total',           value: totalEmpleado,              color: 'warning', raw: false },
                                                                ].map(({ label, value, color, raw, onClick }) => (
                                                                    <div className="col-md" key={label}>
                                                                        <div
                                                                            className={`border-start border-${color} border-3 rounded-end bg-light px-2 py-2 text-center`}
                                                                            style={onClick ? { cursor: 'pointer' } : undefined}
                                                                            onClick={onClick}
                                                                        >
                                                                            <div className="text-muted" style={{ fontSize: '0.72rem', fontWeight: '700' }}>{label}</div>
                                                                            <div className={`fw-bold small text-${color}`}>
                                                                                {raw
                                                                                    ? (value ?? '-')
                                                                                    : formatNumber(value ?? 0, 2, '$', '')}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Observaciones del empleado (editable) */}
                                                            <div className="mb-3">
                                                                {soloLectura ? (
                                                                    <div className="border-start border-warning border-3 ps-3">
                                                                        <div className="text-muted small mb-1">Observaciones del empleado</div>
                                                                        <div>{empDetalle.observaciones || 'Sin observaciones'}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="form-floating">
                                                                        <textarea
                                                                            className="form-control"
                                                                            placeholder="Observaciones del empleado"
                                                                            id={`obs-emp-${empIndex}`}
                                                                            style={{ height: '70px' }}
                                                                            value={empForm?.observaciones || ''}
                                                                            onChange={({ target }) => handleObservacionesEmpleado(empIndex, target.value)}
                                                                        ></textarea>
                                                                        <label htmlFor={`obs-emp-${empIndex}`}>Observaciones del empleado</label>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Conceptos */}
                                                            <div>
                                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                                    <span className="fw-semibold small text-muted">
                                                                        <i className="fas fa-list me-1"></i> Conceptos
                                                                    </span>
                                                                    {!soloLectura && (
                                                                        <button
                                                                            className="btn btn-outline-primary btn-sm"
                                                                            onClick={() => handleAgregarConcepto(empIndex)}
                                                                        >
                                                                            <i className="fas fa-plus me-1"></i> Agregar concepto
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {(empForm?.conceptos || []).length === 0 ? (
                                                                    <p className="text-muted small mb-0">Sin conceptos adicionales.</p>
                                                                ) : soloLectura ? (
                                                                    /* Tabla de solo lectura */
                                                                    <div className="rounded overflow-hidden border">
                                                                        <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                                                                            <thead>
                                                                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                                    <th className="ps-3 py-2 text-muted fw-semibold border-0" style={{ width: '15%' }}>Tipo</th>
                                                                                    <th className="py-2 text-muted fw-semibold border-0">Descripción</th>
                                                                                    <th className="py-2 text-muted fw-semibold border-0 text-end" style={{ width: '18%' }}>Total</th>
                                                                                    <th className="pe-3 py-2 text-muted fw-semibold border-0 text-end" style={{ width: '18%' }}>Fecha</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {(empForm.conceptos).map((concepto, concIndex) => {
                                                                                    const colorMap = { 0: 'success', 1: 'danger', 2: 'danger' };
                                                                                    const color = colorMap[concepto.tipo] || 'secondary';
                                                                                    const esDescuento = concepto.tipo === 1 || concepto.tipo === 2;
                                                                                    return (
                                                                                        <tr key={concIndex} className="border-top">
                                                                                            <td className="ps-3 py-2 align-middle border-0">
                                                                                                <span className={`badge rounded-pill bg-${color} bg-opacity-10 text-${color}`}
                                                                                                    style={{ fontSize: '0.75rem' }}>
                                                                                                    {tiposConcepto.find(t => t.value === concepto.tipo)?.label || '-'}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td className="py-2 align-middle border-0">{concepto.concepto || <span className="text-muted fst-italic">Sin descripción</span>}</td>
                                                                                            <td className={`py-2 align-middle border-0 text-end fw-semibold ${esDescuento ? 'text-danger' : 'text-success'}`}>
                                                                                                {formatNumber(parseFloat(concepto.total) || 0, 2, '$', '')}
                                                                                            </td>
                                                                                            <td className="pe-3 py-2 align-middle border-0 text-end text-muted">
                                                                                                {concepto.fecha ? formatFecha(concepto.fecha) : '-'}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ) : (
                                                                    /* Formulario editable */
                                                                    (empForm.conceptos).map((concepto, concIndex) => (
                                                                        <div key={concIndex} className="row g-2 align-items-start mb-2 pb-2 border-bottom">

                                                                            {/* Tipo */}
                                                                            <div className="col-md-2 form-group">
                                                                                <div className="input-group h-100">
                                                                                    <Select
                                                                                        className="select-group w-100"
                                                                                        classNamePrefix="react-select"
                                                                                        placeholder="Tipo"
                                                                                        options={tiposConcepto}
                                                                                        value={tiposConcepto.find(t => t.value === concepto.tipo) || null}
                                                                                        onChange={(opt) => handleConceptoChange(empIndex, concIndex, 'tipo', opt?.value ?? null)}
                                                                                        menuPortalTarget={document.body}
                                                                                        components={{ ValueContainer: CustomValueContainer }}
                                                                                        styles={{ ...styles_select }}
                                                                                        isClearable
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            {/* Concepto */}
                                                                            <div className="col-md-5">
                                                                                <div className="form-floating">
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control"
                                                                                        placeholder="Concepto"
                                                                                        id={`concepto-${empIndex}-${concIndex}`}
                                                                                        value={concepto.concepto || ''}
                                                                                        onChange={({ target }) => handleConceptoChange(empIndex, concIndex, 'concepto', target.value)}
                                                                                    />
                                                                                    <label htmlFor={`concepto-${empIndex}-${concIndex}`}>Descripción</label>
                                                                                </div>
                                                                            </div>

                                                                            {/* Total */}
                                                                            <div className="col-md-2">
                                                                                <div className="form-floating">
                                                                                    <CurrencyInput
                                                                                        className="form-control"
                                                                                        id={`total-${empIndex}-${concIndex}`}
                                                                                        placeholder="Total"
                                                                                        value={concepto.total || ''}
                                                                                        onValueChange={(value) => handleConceptoChange(empIndex, concIndex, 'total', value || '')}
                                                                                        prefix="$"
                                                                                        decimalsLimit={2}
                                                                                        groupSeparator=","
                                                                                        decimalSeparator="."
                                                                                    />
                                                                                    <label htmlFor={`total-${empIndex}-${concIndex}`}>Total</label>
                                                                                </div>
                                                                            </div>

                                                                            {/* Fecha */}
                                                                            <div className="col-md-2">
                                                                                <div className="form-floating">
                                                                                    <DatePicker
                                                                                        className="form-control text-center input-datepicker"
                                                                                        selected={concepto.fecha}
                                                                                        onChange={(date) => handleConceptoChange(empIndex, concIndex, 'fecha', date)}
                                                                                        dateFormat="dd/MM/yyyy"
                                                                                        showMonthDropdown
                                                                                        showYearDropdown
                                                                                        dropdownMode="select"
                                                                                        autoComplete="off"
                                                                                    />
                                                                                    <label className="label-datepicker">Fecha</label>
                                                                                </div>
                                                                            </div>

                                                                            {/* Eliminar */}
                                                                            <div className="col-md-1 d-flex align-items-center justify-content-center">
                                                                                <button
                                                                                    className="btn btn-sm btn-danger"
                                                                                    onClick={() => handleEliminarConcepto(empIndex, concIndex)}
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>

                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}

                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Botones */}
                    {!soloLectura && (
                        <div className="col-12 mt-3 d-flex justify-content-end mb-3 gap-2">
                            <button
                                className="btn btn-secondary"
                                onClick={() => navigate('/nominas')}
                                disabled={statusProcesar}
                            >
                                Cancelar
                            </button>
                            {statusProcesar
                                ? <button className="btn btn-primary" disabled>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Guardando
                                  </button>
                                : <>
                                    <button className="btn btn-primary" onClick={() => handleSubmit(0)} disabled={statusProcesar || (detalles?.empleados?.length || 0) === 0}>
                                        <i className="fas fa-save me-1"></i>
                                        {esEdicion ? 'Guardar cambios' : 'Crear y guardar'}
                                    </button>
                                    <button className="btn btn-success" onClick={() => handleSubmit(1)} disabled={statusProcesar || (detalles?.empleados?.length || 0) === 0}>
                                        <i className="fas fa-check-circle me-1"></i>
                                        {esEdicion ? 'Guardar y finalizar' : 'Crear y finalizar'}
                                    </button>
                                  </>
                            }
                        </div>
                    )}

                    {soloLectura && (
                        <div className="col-12 mt-3 d-flex justify-content-end mb-3">
                            <button className="btn btn-secondary" onClick={() => navigate('/nominas')}>
                                <i className="fas fa-arrow-left me-1"></i> Volver
                            </button>
                        </div>
                    )}

                    {/* Modal comisiones del empleado */}
                    <Modal show={!!conesEmpleado} size="lg" backdrop="static" centered onHide={() => setConesEmpleado(null)}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <i className="fas fa-dollar-sign text-success me-2"></i>
                                Comisiones — {formatNombre(conesEmpleado?.empleado)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-0">
                            <DataTable
                                columns={[
                                    { name: 'Fecha',               sortable: true, wrap: true,        selector: row => row.fecha || '-' },
                                    { name: 'Conos total',         sortable: true, wrap: true,        selector: row => row.conos_total           ?? '-' },
                                    { name: 'Conos comisionables', sortable: true, wrap: true,        selector: row => row.conos_comisionables   ?? '-' },
                                    { name: 'Bebidas',             sortable: true, wrap: true,        selector: row => row.bebidas               ?? '-' },
                                    { name: 'Total comisión',      sortable: true, wrap: true,        selector: row => row.total_comision != null ? formatNumber(row.total_comision, 2, '$', '') : '-' }
                                ]}
                                data={conesEmpleado?.conos || []}
                                pagination
                                paginationPerPage={20}
                                paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                highlightOnHover
                                noDataComponent={<div className="py-4 text-center text-muted">Sin comisiones registradas en este periodo</div>}
                            />
                        </Modal.Body>
                    </Modal>

                    {/* Modal asistencias del empleado */}
                    <Modal show={!!asistenciaEmpleado} size="lg" backdrop="static" centered onHide={() => setAsistenciaEmpleado(null)}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <i className="fas fa-clipboard-check text-primary me-2"></i>
                                Asistencias — {formatNombre(asistenciaEmpleado?.empleado)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-0">
                            <DataTable
                                columns={[
                                    { name: 'Fecha',        sortable: true, wrap: true,              selector: row => formatFecha(row.fecha) },
                                    { name: 'Responsable',  sortable: true, wrap: true, grow: 2,     selector: row => formatNombre(row.responsable) },
                                    { name: 'Sucursal',     sortable: true, wrap: true, grow: 2,     selector: row => row.sucursal?.nombre || 'Sin definir' },
                                    { name: 'Evento',       sortable: true, wrap: true, grow: 2,     selector: row => row.evento?.nombre   || 'Sin definir' }
                                ]}
                                data={asistenciaEmpleado?.asistencias || []}
                                pagination
                                paginationPerPage={20}
                                paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                highlightOnHover
                                noDataComponent={<div className="py-4 text-center text-muted">Sin asistencias registradas en este periodo</div>}
                            />
                        </Modal.Body>
                    </Modal>
                </>
            )}

        </div>
    );
};

export default FormularioNomina;
